import { CareEnvironment, CarePhase } from '../../../../appModel';
import { appliedHealthCareInflation } from '../../../../appliedHealthCareInflation';
import {
  BuildYearSnapshotInput,
  FinalizeDeathBenefitInput,
  MonthComputeInput,
  PolicyProjectionStrategy,
} from '../strategyTypes';
import { DAYS_IN_MONTH } from '../sharedConstants';
import {
  computeMonthlyCostMetrics,
  computeNon2AdlDaysThisRow,
} from '../sharedEligibility';
import {
  consumeWaitingPeriodDaysForEnvironment,
  getCalendarPolicyYear,
  getCareCostAnchorYear,
  getDailyBenefitAmountForEnvironment,
  getFirstContributingPhaseStartYear,
  getPhaseCareMonthsByPhase,
  getMaxMonthlyBenefitAmount,
  getMonthlyBenefitAmountForEnvironment,
  growthFactor,
} from '../sharedPolicyConfig';
import { calculateClientAge } from '../clientAgeCalculation';
import { deriveStandardMilestones } from '../deriveMilestones';
import {
  deriveIneligibilityReason,
  deriveIneligibleDayBreakdown,
} from '../deriveIneligibilityReason';
import { buildPrePolicyMonth } from '../buildPrePolicyMonth';
import { applyIndemnitySurplus } from '../sharedSurplus';

// ---------------------------------------------------------------------------
// Global Atlantic ForeCare — single-premium fixed annuity with accelerated
// LTC benefits (calculationType `annuityHybrid`). One strategy serves the
// ForeCare single-life type plus the generic `annuityHybrid` type, routed
// here as the closest baseline (joint variants and the AnnuityCare II /
// EquiTrust Bridge products stay on legacy / their own strategies — they
// diverge mechanically; see docs/plans/annuityHybrid-strategy-plan.md).
//
// LEGACY FALLBACK — SINGLE POOL + GUARANTEED-MINIMUM FLOOR:
// - `policyMaximumBenefitAmount` IS the lifetime LTC dollar pool (the Total
//   LTC Benefit = Accelerated Benefit + Maximum Extended Benefit). Like
//   traditional LTC this is a real LTC max, not a death benefit — there is no
//   face / COB split, so `extensionOfBenefitsPool = 0`. We seed
//   `baseBenefitPool` from it (less utilized-to-date).
// - Unlike traditional LTC, ForeCare carries a guaranteed-minimum / Contract
//   Value floor. LTC withdrawals reduce the Contract Value first (as
//   Accelerated Benefits), so the account value IS depleted by LTC draws and
//   the unused remainder is paid as a residual non-LTC payout. We model that
//   via the universal `deathBenefit` pool slot: seeded to
//   `policyMinimumGuaranteedBenefitAmount`, depleted by `drawFromPool`, and
//   floored at 0 (the residual can't go negative). `finalizeDeathBenefit`
//   returns the residual. This reproduces legacy
//   `policyTotalValue = max(coverage, floor)` /
//   `policyNonLtcPayout = max(0, floor − coverage)` when summed at finalize.
//
// PAYOUT MODEL — reimbursement (default; ForeCare) or cash indemnity:
//   identical to traditionalLtcStrategy. Reimbursement pays
//   `min(eligibleCost, proRatedCap)` from the pool; indemnity pays
//   `min(proRatedCap, pool)` as cash regardless of cost.
//
// GUARANTEED-RATE MODEL:
// - New uploads can opt into a guaranteed-only carrier-ledger model with
//   year-1 Accelerated Benefit / Contract Value, year-1 Maximum Extended
//   Benefit, guaranteed crediting rates, and the LTC rider monthly charge.
// - In that mode the universal `baseBenefitPool` carries the Contract Value /
//   Accelerated Benefit layer, `extensionOfBenefitsPool` carries the Maximum
//   Extended Benefit layer, and `deathBenefit` mirrors the remaining Contract
//   Value residual. LTC draws deplete Contract Value first, then Extended
//   Benefits. Interest is credited monthly on remaining Contract Value; rider
//   charges are waived in months with LTC withdrawals and otherwise cannot
//   reduce the no-claim month-end Contract Value below the prior month-end
//   value.
//
// INFLATION:
// - The sample guaranteed illustration does not elect the LTC Inflation Rider,
//   so its Maximum Monthly Benefit and Maximum Extended Benefit stay flat. When
//   a guaranteed ForeCare row has a non-zero `policyInflationProtection`, the
//   rider grows the Maximum Monthly Benefit and unused Maximum Extended Benefit
//   only; Contract Value / Accelerated Benefit stays on the guaranteed ledger.
//   The old fallback path also honors a non-zero `policyInflationProtection`
//   value for legacy/generic annuityHybrid rows.
//
// 2-ADL GATE + WAITING PERIOD: standard; EP days are strictly out-of-pocket,
// no retroactive lump.
// ---------------------------------------------------------------------------

export interface ForecareState {
  // Indemnity-surplus reserve. Indemnity policies pay a fixed monthly benefit
  // regardless of cost; the excess above eligible cost is banked here and
  // drained on later shortfall months (legacy `applyIndemnityPayoutToPhase`
  // parity). Always 0 on reimbursement-election policies (their payout never
  // exceeds eligible cost), so the surplus model is a numeric no-op there.
  indemnitySurplus: number;
}
export interface ForecareDeltas {
  contractValueInterestCredit: number;
  contractValueRiderCharge: number;
  acceleratedBenefitDraw: number;
  extendedBenefitDraw: number;
  indemnitySurplusAccrued: number;
  indemnitySurplusApplied: number;
}

const ACCELERATED_BENEFIT_PERIOD_MONTHS = 24;
const EXTENDED_BENEFIT_PERIOD_MONTHS = 48;

export interface ForecareGuaranteedConfig {
  usesGuaranteedRateModel: boolean;
  acceleratedAtIssue: number;
  extendedAtIssue: number;
  contractValueAtIssue: number;
  acceleratedBenefitPeriodMonths: number;
  extendedBenefitPeriodMonths: number;
  guaranteedInitialRate: number;
  guaranteedInitialRateYears: number;
  guaranteedRenewalRate: number;
  monthlyRiderChargeRate: number;
  monthlyBenefitCapAtIssue: number;
  ltcInflationRate: number;
  ltcInflationCapYears: number;
  ltcInflationIsSimple: boolean;
}

interface InflationConfig {
  rate: number;
  capYears: number;
  isSimple: boolean;
}

function readInflation(ltcPolicy: PolicyFundingSource): InflationConfig {
  const capYearsRaw = ltcPolicy.policyInflationProtectionYears;
  return {
    rate: ltcPolicy.policyInflationProtection ?? 0,
    capYears: capYearsRaw && capYearsRaw > 0 ? capYearsRaw : 0,
    isSimple: ltcPolicy.isSimpleInflationProtection === true,
  };
}

function emptyForecareDeltas(): ForecareDeltas {
  return {
    contractValueInterestCredit: 0,
    contractValueRiderCharge: 0,
    acceleratedBenefitDraw: 0,
    extendedBenefitDraw: 0,
    indemnitySurplusAccrued: 0,
    indemnitySurplusApplied: 0,
  };
}

function isFiniteNumber(value: number | null | undefined): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

export function shouldUseForecareGuaranteedRateModel(
  ltcPolicy: PolicyFundingSource,
): boolean {
  return (
    isFiniteNumber(ltcPolicy.policyBaseBenefitAmountAtIssue) &&
    isFiniteNumber(ltcPolicy.policyContinuationBenefitAmountAtIssue) &&
    isFiniteNumber(ltcPolicy.policyBaseBenefitGuaranteedInitialRate) &&
    isFiniteNumber(ltcPolicy.policyBaseBenefitGuaranteedInitialRateYears) &&
    isFiniteNumber(ltcPolicy.policyBaseBenefitGuaranteedRenewalRate) &&
    isFiniteNumber(ltcPolicy.policyMonthlyContinuationBenefitChargeRate)
  );
}

function readPositiveMonths(value: number | null | undefined, fallback: number): number {
  return isFiniteNumber(value) && value > 0 ? value : fallback;
}

function readGuaranteedConfig(ltcPolicy: PolicyFundingSource): ForecareGuaranteedConfig {
  const usesGuaranteedRateModel = shouldUseForecareGuaranteedRateModel(ltcPolicy);
  const inflation = readInflation(ltcPolicy);
  const acceleratedAtIssue = usesGuaranteedRateModel
    ? ltcPolicy.policyBaseBenefitAmountAtIssue!
    : (ltcPolicy.policyMinimumGuaranteedBenefitAmount ??
      ltcPolicy.policyBaseBenefitAmountAtIssue ??
      0);
  const extendedAtIssue = usesGuaranteedRateModel
    ? ltcPolicy.policyContinuationBenefitAmountAtIssue!
    : (ltcPolicy.policyContinuationBenefitAmount ?? 0);
  const contractValueAtIssue = usesGuaranteedRateModel
    ? (ltcPolicy.policyAccumulatedValueAtIssue ?? acceleratedAtIssue)
    : (ltcPolicy.policyMinimumGuaranteedBenefitAmount ?? 0);

  return {
    usesGuaranteedRateModel,
    acceleratedAtIssue,
    extendedAtIssue,
    contractValueAtIssue,
    acceleratedBenefitPeriodMonths: readPositiveMonths(
      ltcPolicy.policyAccelerationBenefitDurationMonths,
      ACCELERATED_BENEFIT_PERIOD_MONTHS,
    ),
    extendedBenefitPeriodMonths: readPositiveMonths(
      ltcPolicy.policyEobBenefitDurationMonths,
      EXTENDED_BENEFIT_PERIOD_MONTHS,
    ),
    guaranteedInitialRate: usesGuaranteedRateModel
      ? ltcPolicy.policyBaseBenefitGuaranteedInitialRate!
      : 0,
    guaranteedInitialRateYears: usesGuaranteedRateModel
      ? ltcPolicy.policyBaseBenefitGuaranteedInitialRateYears!
      : 0,
    guaranteedRenewalRate: usesGuaranteedRateModel
      ? ltcPolicy.policyBaseBenefitGuaranteedRenewalRate!
      : 0,
    monthlyRiderChargeRate: usesGuaranteedRateModel
      ? ltcPolicy.policyMonthlyContinuationBenefitChargeRate!
      : 0,
    monthlyBenefitCapAtIssue: getMaxMonthlyBenefitAmount(ltcPolicy),
    ltcInflationRate: inflation.rate,
    ltcInflationCapYears: inflation.capYears,
    ltcInflationIsSimple: inflation.isSimple,
  };
}

function rateForContractYear(
  initialRate: number,
  initialRateYears: number,
  renewalRate: number,
  contractYear: number,
): number {
  return contractYear <= initialRateYears ? initialRate : renewalRate;
}

function monthlyEffectiveRate(annualRate: number): number {
  return Math.pow(1 + annualRate, 1 / 12) - 1;
}

function normalizedPolicyYear(policyYear: number): number {
  return Math.max(Math.floor(policyYear), 1);
}

export function scheduledForecareValues(
  config: ForecareGuaranteedConfig,
  policyYear: number,
): {
  contractValue: number;
  acceleratedBenefit: number;
  maximumExtendedBenefit: number;
  totalLtcBenefit: number;
  monthlyBenefitCap: number;
} {
  const targetYear = normalizedPolicyYear(policyYear);
  let contractValue = config.contractValueAtIssue;

  if (config.usesGuaranteedRateModel) {
    for (let contractYear = 2; contractYear <= targetYear; contractYear += 1) {
      const monthlyRate = monthlyEffectiveRate(
        rateForContractYear(
          config.guaranteedInitialRate,
          config.guaranteedInitialRateYears,
          config.guaranteedRenewalRate,
          contractYear,
        ),
      );

      for (let month = 0; month < 12; month += 1) {
        const openValue = contractValue;
        const riderCharge = contractValue * config.monthlyRiderChargeRate;
        const afterCharge = Math.max(contractValue - riderCharge, 0);
        const afterInterest = afterCharge * (1 + monthlyRate);
        contractValue = Math.max(afterInterest, openValue);
      }
    }
  }

  const ltcRiderFactor = ltcRiderGrowthFactor(config, targetYear);
  const maximumExtendedBenefit = config.extendedAtIssue * ltcRiderFactor;
  const monthlyBenefitCap = config.monthlyBenefitCapAtIssue * ltcRiderFactor;

  return {
    contractValue,
    acceleratedBenefit: contractValue,
    maximumExtendedBenefit,
    totalLtcBenefit: contractValue + maximumExtendedBenefit,
    monthlyBenefitCap,
  };
}

function getAtIssuePool(ltcPolicy: PolicyFundingSource): number {
  return ltcPolicy.policyMaximumBenefitAmount ?? 0;
}

// Pool-growth factor at a given policy year — the shared `growthFactor`
// convention: a capYears-year rider applies capYears increases on both the
// simple and compound branches (the last one entering policy year
// capYears + 1), then freezes.
function poolGrowthFactor(
  { rate, capYears, isSimple }: InflationConfig,
  policyYear: number,
): number {
  return growthFactor(rate, capYears, isSimple, policyYear);
}

function ltcRiderInflation(config: ForecareGuaranteedConfig): InflationConfig {
  return {
    rate: config.ltcInflationRate,
    capYears: config.ltcInflationCapYears,
    isSimple: config.ltcInflationIsSimple,
  };
}

function ltcRiderGrowthFactor(
  config: ForecareGuaranteedConfig,
  policyYear: number,
): number {
  return poolGrowthFactor(ltcRiderInflation(config), policyYear);
}

function calcExtendedBenefitInflationAccrual(
  config: ForecareGuaranteedConfig,
  openExtendedBenefit: number,
  priorPolicyYear: number,
  policyYear: number,
): number {
  if (openExtendedBenefit <= 0 || config.ltcInflationRate <= 0) return 0;

  const priorFactor = ltcRiderGrowthFactor(config, priorPolicyYear);
  const currentFactor = ltcRiderGrowthFactor(config, policyYear);
  if (currentFactor <= priorFactor) return 0;

  if (config.ltcInflationIsSimple) {
    return config.extendedAtIssue * (currentFactor - priorFactor);
  }

  return openExtendedBenefit * (currentFactor / priorFactor - 1);
}

export const forecareStrategy: PolicyProjectionStrategy<ForecareState, ForecareDeltas> = {
  initClaimState(client, ltcPolicy): MonthlyState<ForecareState> {
    const guaranteedConfig = readGuaranteedConfig(ltcPolicy);
    if (guaranteedConfig.usesGuaranteedRateModel) {
      const utilizedToDate = ltcPolicy.policyBenefitUtilizedToDate ?? 0;
      const claimStartPolicyYear = getCalendarPolicyYear(client as Client, 0);
      const scheduled = scheduledForecareValues(guaranteedConfig, claimStartPolicyYear);
      const acceleratedUtilized = Math.min(utilizedToDate, scheduled.acceleratedBenefit);
      const extendedUtilized = Math.max(utilizedToDate - scheduled.acceleratedBenefit, 0);

      return {
        pools: {
          baseBenefitPool: Math.max(scheduled.acceleratedBenefit - acceleratedUtilized, 0),
          extensionOfBenefitsPool: Math.max(
            scheduled.maximumExtendedBenefit - extendedUtilized,
            0,
          ),
          deathBenefit: Math.max(scheduled.contractValue - acceleratedUtilized, 0),
        },
        waitingPeriod: {
          homeDays:
            ltcPolicy.homeCareWaitingPeriodDaysRemaining ??
            ltcPolicy.homeCareWaitingPeriodDays ??
            0,
          facilityDays:
            ltcPolicy.facilityCareWaitingPeriodDaysRemaining ??
            ltcPolicy.facilityCareWaitingPeriodDays ??
            0,
        },
        strategyState: { indemnitySurplus: 0 },
      };
    }

    const atIssuePool = getAtIssuePool(ltcPolicy);
    const utilizedToDate = ltcPolicy.policyBenefitUtilizedToDate ?? 0;
    const minGuaranteed = ltcPolicy.policyMinimumGuaranteedBenefitAmount ?? 0;
    const inflation = readInflation(ltcPolicy);
    // Anniversaries between policy issue and claim start are not iterated (the
    // projection only spans claim months), so pre-inflate the pool to its
    // value at claim start. Claims starting in policy year 1 keep the factor
    // at 1.0. (For ForeCare the extracted pool already embeds account-value
    // growth at the event age, so at 0% this is a no-op; correct for a rated
    // ForeCare with the inflation rider elected.)
    const claimStartPolicyYear = getCalendarPolicyYear(client as Client, 0);
    const poolAtClaimStart = atIssuePool * poolGrowthFactor(inflation, claimStartPolicyYear);
    return {
      pools: {
        baseBenefitPool: Math.max(poolAtClaimStart - utilizedToDate, 0),
        // No continuation-of-benefits pool.
        extensionOfBenefitsPool: 0,
        // Seed the guaranteed-minimum / Contract Value floor. LTC draws deplete
        // it; the residual after the claim is the non-LTC payout.
        deathBenefit: minGuaranteed,
      },
      waitingPeriod: {
        homeDays:
          ltcPolicy.homeCareWaitingPeriodDaysRemaining ??
          ltcPolicy.homeCareWaitingPeriodDays ??
          0,
        facilityDays:
          ltcPolicy.facilityCareWaitingPeriodDaysRemaining ??
          ltcPolicy.facilityCareWaitingPeriodDays ??
          0,
      },
      strategyState: { indemnitySurplus: 0 },
    };
  },

  buildClaimTerms(client, ltcPolicy): ClaimTerms {
    const guaranteedConfig = readGuaranteedConfig(ltcPolicy);
    const baseMonthly = guaranteedConfig.usesGuaranteedRateModel
      ? guaranteedConfig.monthlyBenefitCapAtIssue
      : getMaxMonthlyBenefitAmount(ltcPolicy);
    const dailyBenefit = baseMonthly / DAYS_IN_MONTH;
    const inflation = readInflation(ltcPolicy);
    const clientAsClient = client as Client;
    const projectionAnchorYear = getCareCostAnchorYear(clientAsClient);
    const isIndemnity = ltcPolicy.isIndemnityPolicyPayment === true;
    const utilizedToDate = ltcPolicy.policyBenefitUtilizedToDate ?? 0;
    const claimStartPolicyYear = getCalendarPolicyYear(clientAsClient, 0);

    if (guaranteedConfig.usesGuaranteedRateModel) {
      // Prior utilization deducted across the Accelerated then Extended pools at
      // claim start (mirrors `initClaimState`). The accelerated portion is
      // capped at the scheduled Accelerated Benefit; the spillover is capped at
      // the Maximum Extended Benefit. The Contract Value floor is a separate
      // concern and does not feed "Drawn to date".
      const scheduled = scheduledForecareValues(guaranteedConfig, claimStartPolicyYear);
      const acceleratedUtilized = Math.min(utilizedToDate, scheduled.acceleratedBenefit);
      const extendedUtilized = Math.min(
        Math.max(utilizedToDate - scheduled.acceleratedBenefit, 0),
        scheduled.maximumExtendedBenefit,
      );
      const priorUtilization = acceleratedUtilized + extendedUtilized;
      return {
        strategyId: 'forecare',
        priorUtilization,
        nominalDailyBenefit: dailyBenefit,
        nominalDailyBenefitBase: dailyBenefit,
        nominalDailyBenefitByEnvironment: {
          [CareEnvironment.home]: ltcPolicy.dailyHomeBenefitAmount ?? 0,
          [CareEnvironment.assistedLiving]: ltcPolicy.dailyAssistedLivingBenefitAmount ?? 0,
          [CareEnvironment.fullCareFacility]: ltcPolicy.dailyNursingHomeBenefitAmount ?? 0,
          [CareEnvironment.independentLiving]: getDailyBenefitAmountForEnvironment(
            ltcPolicy,
            CareEnvironment.independentLiving,
          ),
        },
        inflationRate: guaranteedConfig.ltcInflationRate,
        inflationCapYears: guaranteedConfig.ltcInflationCapYears,
        inflationIsSimple: guaranteedConfig.ltcInflationIsSimple,
        baseBenefitPoolInflationRate: 0,
        baseBenefitPoolInflationCapYears: 0,
        extensionOfBenefitsPoolInflationRate: guaranteedConfig.ltcInflationRate,
        extensionOfBenefitsPoolInflationCapYears: guaranteedConfig.ltcInflationCapYears,
        baseBenefitPoolAtIssue: guaranteedConfig.contractValueAtIssue,
        extensionOfBenefitsPoolAtIssue: guaranteedConfig.extendedAtIssue,
        baseBenefitPoolDurationMonths: guaranteedConfig.acceleratedBenefitPeriodMonths,
        extensionOfBenefitsPoolDurationMonths: guaranteedConfig.extendedBenefitPeriodMonths,
        baseBenefitGuaranteedInitialRate: guaranteedConfig.guaranteedInitialRate,
        baseBenefitGuaranteedInitialRateYears: guaranteedConfig.guaranteedInitialRateYears,
        baseBenefitGuaranteedRenewalRate: guaranteedConfig.guaranteedRenewalRate,
        monthlyContinuationBenefitChargeRate: guaranteedConfig.monthlyRiderChargeRate,
        accumulatedValueAtIssue: guaranteedConfig.contractValueAtIssue,
        accumulatedValueGuaranteedInitialRate: guaranteedConfig.guaranteedInitialRate,
        accumulatedValueGuaranteedInitialRateYears: guaranteedConfig.guaranteedInitialRateYears,
        accumulatedValueGuaranteedRenewalRate: guaranteedConfig.guaranteedRenewalRate,
        careCostInflationRate: appliedHealthCareInflation(clientAsClient),
        projectionAnchorYear,
        phaseStartYears: { ...(clientAsClient.clientPhasePredictedStartYears ?? {}) } as Record<
          CarePhase,
          number | null
        >,
        phaseCareMonthsByPhase: getPhaseCareMonthsByPhase(clientAsClient),
        firstPhaseStartYear: getFirstContributingPhaseStartYear(clientAsClient),
        policyPremiumStartYear: ltcPolicy.policyPremiumStartYear ?? projectionAnchorYear,
        minimumGuaranteedDeathBenefit: guaranteedConfig.contractValueAtIssue,
        isReimbursement: !isIndemnity,
        isIndemnity,
      };
    }

    // Fallback single-pool model: prior utilization deducted from the base pool
    // only, capped at the pre-inflated pool at claim start (mirrors
    // `initClaimState`'s `Math.max(poolAtClaimStart − utilized, 0)`).
    const fallbackPoolAtClaimStart =
      getAtIssuePool(ltcPolicy) * poolGrowthFactor(inflation, claimStartPolicyYear);
    const fallbackPriorUtilization = Math.min(utilizedToDate, fallbackPoolAtClaimStart);
    return {
      strategyId: 'forecare',
      priorUtilization: fallbackPriorUtilization,
      nominalDailyBenefit: dailyBenefit,
      nominalDailyBenefitBase: dailyBenefit,
      nominalDailyBenefitByEnvironment: {
        [CareEnvironment.home]: ltcPolicy.dailyHomeBenefitAmount ?? 0,
        [CareEnvironment.assistedLiving]: ltcPolicy.dailyAssistedLivingBenefitAmount ?? 0,
        [CareEnvironment.fullCareFacility]: ltcPolicy.dailyNursingHomeBenefitAmount ?? 0,
        [CareEnvironment.independentLiving]: getDailyBenefitAmountForEnvironment(
          ltcPolicy,
          CareEnvironment.independentLiving,
        ),
      },
      // Single product-wide inflation rate drives both the daily-cap step and
      // the pool growth. No separate face/COB rate — leave the EOB knobs unset.
      inflationRate: inflation.rate,
      inflationCapYears: inflation.capYears,
      inflationIsSimple: inflation.isSimple,
      baseBenefitPoolInflationRate: inflation.rate,
      baseBenefitPoolInflationCapYears: inflation.capYears,
      baseBenefitPoolAtIssue: getAtIssuePool(ltcPolicy),
      careCostInflationRate: appliedHealthCareInflation(clientAsClient),
      projectionAnchorYear,
      phaseStartYears: { ...(clientAsClient.clientPhasePredictedStartYears ?? {}) } as Record<
        CarePhase,
        number | null
      >,
      phaseCareMonthsByPhase: getPhaseCareMonthsByPhase(clientAsClient),
      firstPhaseStartYear: getFirstContributingPhaseStartYear(clientAsClient),
      policyPremiumStartYear: ltcPolicy.policyPremiumStartYear ?? projectionAnchorYear,
      // The guaranteed-minimum / Contract Value floor surfaced as the residual.
      minimumGuaranteedDeathBenefit: ltcPolicy.policyMinimumGuaranteedBenefitAmount ?? 0,
      isReimbursement: !isIndemnity,
      isIndemnity,
    };
  },

  buildYearSnapshot(input: BuildYearSnapshotInput): YearSnapshot {
    const { ltcPolicy, policyYear } = input;
    const guaranteedConfig = readGuaranteedConfig(ltcPolicy);
    if (guaranteedConfig.usesGuaranteedRateModel) {
      const scheduled = scheduledForecareValues(guaranteedConfig, policyYear);
      return {
        policyYear,
        effectiveDailyBenefit: scheduled.monthlyBenefitCap / DAYS_IN_MONTH,
        effectiveDailyBenefitBase: scheduled.monthlyBenefitCap / DAYS_IN_MONTH,
        effectiveDailyBenefitRider: scheduled.monthlyBenefitCap / DAYS_IN_MONTH,
        effectiveFaceAmount: scheduled.acceleratedBenefit,
        effectiveContinuationBenefit: scheduled.maximumExtendedBenefit,
        effectiveTotalLtcBenefit: scheduled.totalLtcBenefit,
        monthlyBenefitCap: scheduled.monthlyBenefitCap,
        continuationMonthlyBenefitCap: scheduled.monthlyBenefitCap,
        theoreticalLtcPoolCeiling: scheduled.totalLtcBenefit,
      };
    }

    const baseMonthly = getMaxMonthlyBenefitAmount(ltcPolicy);
    const inflation = readInflation(ltcPolicy);
    const factor = poolGrowthFactor(inflation, policyYear);

    const atIssuePool = getAtIssuePool(ltcPolicy);
    const inflatedMonthlyCap = baseMonthly * factor;
    // No-claims "ceiling" framing: the inflated LTC pool. The guaranteed-
    // minimum floor is a finalize concern, not a year-snapshot ceiling.
    const inflatedPool = atIssuePool * factor;

    return {
      policyYear,
      effectiveDailyBenefit: inflatedMonthlyCap / DAYS_IN_MONTH,
      effectiveDailyBenefitBase: inflatedMonthlyCap / DAYS_IN_MONTH,
      effectiveDailyBenefitRider: 0,
      // No life face, no continuation pool.
      effectiveFaceAmount: 0,
      effectiveContinuationBenefit: 0,
      effectiveTotalLtcBenefit: inflatedPool,
      monthlyBenefitCap: inflatedMonthlyCap,
      theoreticalLtcPoolCeiling: inflatedPool,
    };
  },

  computeMonth(
    input: MonthComputeInput<ForecareState, ForecareDeltas>,
  ): MonthlyEntry<ForecareState, ForecareDeltas> {
    const { client, ltcPolicy, phaseCarePlan, monthlyCareInput, openState, priorMonth } = input;
    const { monthIndex, carePhase, monthsIntoPhase } = monthlyCareInput;
    const clientAsClient = client as Client;

    const costMetrics = computeMonthlyCostMetrics(clientAsClient, phaseCarePlan, monthsIntoPhase);
    const claimYearOffset = Math.floor(monthIndex / 12);
    const policyYear = getCalendarPolicyYear(clientAsClient, monthIndex);

    const context: MonthContext = {
      monthIndex,
      policyYear,
      clientAge: calculateClientAge(clientAsClient.intakeSurvey?.clientAge, policyYear),
      claimYearOffset,
      carePhase,
      monthsIntoPhase,
      careEnvironment: phaseCarePlan.appliedCareEnvironment,
    };

    // Pre-policy gate — a care month before the premium-start year pays $0,
    // full OOP. Single-life floors its policy year at 1, so this is defensive.
    if (policyYear < 1) {
      const prePolicyFacts: MonthlyCareFacts = {
        nominalCareCost: costMetrics.nominalCareCost,
        effectiveCareCost: costMetrics.effectiveCareCost,
        dailyCareCost: costMetrics.dailyCareCost,
        careCostInflationYears: costMetrics.careCostInflationYears,
        projectedCareYear: costMetrics.projectedCareYear,
        non2AdlDays: 0,
        coverageEligibleDays: 0,
        waitingPeriodDaysConsumedHome: 0,
        waitingPeriodDaysConsumedFacility: 0,
        appliedMonthlyBenefitCap: 0,
        appliedCapPoolSource: 'base',
      };
      return buildPrePolicyMonth<ForecareState, ForecareDeltas>({
        context,
        facts: prePolicyFacts,
        openState,
        strategyDeltas: emptyForecareDeltas(),
      });
    }

    const guaranteedConfig = readGuaranteedConfig(ltcPolicy);
    if (guaranteedConfig.usesGuaranteedRateModel) {
      // 1. Day classification (2-ADL bucket → WP → eligible).
      const non2AdlDays = computeNon2AdlDaysThisRow(clientAsClient, phaseCarePlan, monthsIntoPhase);
      const daysAfter2Adl = DAYS_IN_MONTH - non2AdlDays;

      const wpConsumption = consumeWaitingPeriodDaysForEnvironment(
        openState.waitingPeriod,
        phaseCarePlan.appliedCareEnvironment,
        daysAfter2Adl,
      );
      const {
        wpDaysThisRow,
        closeHomeWp,
        closeFacilityWp,
        waitingPeriodDaysConsumedHome,
        waitingPeriodDaysConsumedFacility,
        closeRelevantWp,
      } = wpConsumption;
      const eligibleDays = daysAfter2Adl - wpDaysThisRow;

      // The LTC Inflation Rider grows the current month's Maximum Monthly
      // Benefit and the remaining Maximum Extended Benefit, but not the
      // Contract Value / Accelerated Benefit layer.
      const monthlyCap =
        guaranteedConfig.monthlyBenefitCapAtIssue *
        ltcRiderGrowthFactor(guaranteedConfig, policyYear);
      const proRatedCap = monthlyCap * (eligibleDays / DAYS_IN_MONTH);
      const eligibleCost = costMetrics.dailyCareCost * eligibleDays;

      let extendedBenefitInflationAccrual = 0;
      if (priorMonth && policyYear !== priorMonth.context.policyYear) {
        extendedBenefitInflationAccrual = calcExtendedBenefitInflationAccrual(
          guaranteedConfig,
          openState.pools.extensionOfBenefitsPool,
          priorMonth.context.policyYear,
          policyYear,
        );
      }
      const extendedBenefitAfterAccrual =
        openState.pools.extensionOfBenefitsPool + extendedBenefitInflationAccrual;

      const isIndemnity = ltcPolicy.isIndemnityPolicyPayment === true;
      const totalAvailable =
        openState.pools.baseBenefitPool + extendedBenefitAfterAccrual;
      const wouldPay = isIndemnity ? proRatedCap : Math.min(eligibleCost, proRatedCap);
      const targetDraw = eligibleDays > 0 ? Math.min(wouldPay, totalAvailable) : 0;
      const drawFromAccelerated = Math.min(targetDraw, openState.pools.baseBenefitPool);
      const drawFromExtended = Math.min(
        targetDraw - drawFromAccelerated,
        extendedBenefitAfterAccrual,
      );
      // Gross cash this row pays out across both layers. On indemnity this is
      // the capped cash regardless of cost; on reimbursement it is the
      // reimbursed amount (≤ eligible cost). The layers deplete by this gross
      // cash; the surplus model is a separate accounting reserve on top.
      const rowPayout = drawFromAccelerated + drawFromExtended;
      const indemnityPaid = isIndemnity ? rowPayout : 0;

      // Indemnity-surplus accounting (per-row, unconditional). Cost-coverage is
      // capped at eligible cost; cash above it banks as surplus and drains on
      // later shortfall rows. On reimbursement-election policies the open
      // surplus is always 0 and the reimbursed payout never exceeds eligible
      // cost, so this is a numeric no-op (no accrual/drain).
      const surplusResult = applyIndemnitySurplus({
        openSurplus: openState.strategyState.indemnitySurplus,
        indemnityPayout: rowPayout,
        eligibleCost,
        fullCost: costMetrics.effectiveCareCost,
        // Two layers (Accelerated + Extended): exhausted only when BOTH are spent.
        policyExhausted: totalAvailable <= 0,
      });

      const contractValueAfterDraw = openState.pools.baseBenefitPool - drawFromAccelerated;
      const withdrawalMade = rowPayout > 0;
      const monthlyContractRate = monthlyEffectiveRate(
        rateForContractYear(
          guaranteedConfig.guaranteedInitialRate,
          guaranteedConfig.guaranteedInitialRateYears,
          guaranteedConfig.guaranteedRenewalRate,
          policyYear,
        ),
      );

      let contractValueRiderCharge = 0;
      let contractValueInterestCredit = 0;
      let closeContractValue = contractValueAfterDraw;
      if (contractValueAfterDraw > 0) {
        if (withdrawalMade) {
          contractValueInterestCredit = contractValueAfterDraw * monthlyContractRate;
          closeContractValue = contractValueAfterDraw + contractValueInterestCredit;
        } else {
          contractValueRiderCharge =
            contractValueAfterDraw * guaranteedConfig.monthlyRiderChargeRate;
          const afterCharge = Math.max(contractValueAfterDraw - contractValueRiderCharge, 0);
          contractValueInterestCredit = afterCharge * monthlyContractRate;
          closeContractValue = Math.max(
            afterCharge + contractValueInterestCredit,
            contractValueAfterDraw,
          );
        }
      }

      const closeExtendedBenefit = extendedBenefitAfterAccrual - drawFromExtended;
      const closeDeathBenefit = closeContractValue;
      const deathBenefitReduction = openState.pools.deathBenefit - closeDeathBenefit;
      const deathBenefitFloored =
        openState.pools.deathBenefit > 0 &&
        closeDeathBenefit === 0 &&
        drawFromAccelerated > openState.pools.deathBenefit;
      const contractValueNetAccrual = closeContractValue - contractValueAfterDraw;
      const inExtendedPhase =
        drawFromExtended > 0 ||
        (openState.pools.baseBenefitPool <= 0 && drawFromAccelerated === 0);

      const facts: MonthlyCareFacts = {
        nominalCareCost: costMetrics.nominalCareCost,
        effectiveCareCost: costMetrics.effectiveCareCost,
        dailyCareCost: costMetrics.dailyCareCost,
        careCostInflationYears: costMetrics.careCostInflationYears,
        projectedCareYear: costMetrics.projectedCareYear,
        non2AdlDays,
        coverageEligibleDays: eligibleDays,
        waitingPeriodDaysConsumedHome,
        waitingPeriodDaysConsumedFacility,
        appliedMonthlyBenefitCap: monthlyCap,
        appliedCapPoolSource: inExtendedPhase ? 'eob' : 'base',
      };

      const payout: PayoutSummary = {
        ltcCoveragePaid: surplusResult.ltcCoveragePaid,
        ltcPayoutToCost: surplusResult.ltcPayoutToCost,
        ineligibleCost: costMetrics.effectiveCareCost - surplusResult.ltcPayoutToCost,
        indemnityPayout: indemnityPaid,
      };

      const deltas: MonthlyDeltas<ForecareDeltas> = {
        payout,
        base: { depletion: drawFromAccelerated, inflationAccrual: contractValueNetAccrual },
        eob: {
          depletion: drawFromExtended,
          inflationAccrual: extendedBenefitInflationAccrual,
        },
        deathBenefit: {
          reduction: deathBenefitReduction,
          flooredAtMinimum: deathBenefitFloored,
        },
        strategyDeltas: {
          contractValueInterestCredit,
          contractValueRiderCharge,
          acceleratedBenefitDraw: drawFromAccelerated,
          extendedBenefitDraw: drawFromExtended,
          indemnitySurplusAccrued: surplusResult.surplusAccrued,
          indemnitySurplusApplied: surplusResult.surplusApplied,
        },
      };

      const closeState: MonthlyState<ForecareState> = {
        pools: {
          baseBenefitPool: closeContractValue,
          extensionOfBenefitsPool: closeExtendedBenefit,
          deathBenefit: closeDeathBenefit,
        },
        waitingPeriod: { homeDays: closeHomeWp, facilityDays: closeFacilityWp },
        strategyState: { indemnitySurplus: surplusResult.closeSurplus },
      };

      const status: MonthlyStatus = {
        ltcPoolExhausted: closeContractValue + closeExtendedBenefit <= 0,
        clientPoolExhausted: closeContractValue + closeExtendedBenefit <= 0,
        epSatisfied: closeRelevantWp === 0,
      };

      const milestones = deriveStandardMilestones<ForecareState, ForecareDeltas>({
        context,
        facts,
        openState,
        closeState,
        deltas,
        priorMonth,
        retroactiveLumpPaid: 0,
        // The residual Contract Value can move down to $0 and can also grow by
        // guaranteed interest during claim, so the generic minimum-DB milestone
        // is not the right semantics in the guaranteed ledger branch.
        emitMinimumDeathBenefitMilestone: false,
        clientHasStartedLtc: clientAsClient.intakeSurvey?.clientHasStartedLtc ?? false,
        hasInflationProtection:
          guaranteedConfig.usesGuaranteedRateModel ||
          contractValueNetAccrual > 0 ||
          extendedBenefitInflationAccrual > 0,
      });

      const ineligibilityInput = {
        careEnvironment: phaseCarePlan.appliedCareEnvironment,
        facts,
        openState,
        closeState,
        payout,
      };
      const ineligibilityReason =
        deriveIneligibilityReason<ForecareState>(ineligibilityInput);
      const ineligibleDayBreakdown =
        deriveIneligibleDayBreakdown<ForecareState>(ineligibilityInput);

      return {
        context,
        facts,
        openState,
        state: closeState,
        deltas,
        events: { status, milestones, ineligibilityReason, ineligibleDayBreakdown },
      };
    }

    // 1. Day classification (2-ADL bucket → WP → eligible).
    const non2AdlDays = computeNon2AdlDaysThisRow(clientAsClient, phaseCarePlan, monthsIntoPhase);
    const daysAfter2Adl = DAYS_IN_MONTH - non2AdlDays;

    const wpConsumption = consumeWaitingPeriodDaysForEnvironment(
      openState.waitingPeriod,
      phaseCarePlan.appliedCareEnvironment,
      daysAfter2Adl,
    );
    const {
      wpDaysThisRow,
      closeHomeWp,
      closeFacilityWp,
      waitingPeriodDaysConsumedHome,
      waitingPeriodDaysConsumedFacility,
      closeRelevantWp,
    } = wpConsumption;
    const eligibleDays = daysAfter2Adl - wpDaysThisRow;

    // 2. Per-month dollar cap (this year's inflation factor).
    const inflation = readInflation(ltcPolicy);
    const baseMonthly = getMonthlyBenefitAmountForEnvironment(
      ltcPolicy,
      phaseCarePlan.appliedCareEnvironment,
    );
    const monthlyCap = baseMonthly * poolGrowthFactor(inflation, policyYear);

    // 3. Annual anniversary pool accrual on the REMAINING balance. Gated on
    //    "pool still > 0" and rate > 0. A capYears-year rider applies capYears
    //    increases, the last one on the anniversary ENTERING policy year
    //    capYears + 1 — matching the snapshot factors from `growthFactor`.
    let inflationAccrual = 0;
    if (priorMonth && policyYear !== priorMonth.context.policyYear) {
      const withinCap = inflation.capYears <= 0 || policyYear <= inflation.capYears + 1;
      if (openState.pools.baseBenefitPool > 0 && withinCap && inflation.rate > 0) {
        inflationAccrual = inflation.isSimple
          ? getAtIssuePool(ltcPolicy) * inflation.rate
          : openState.pools.baseBenefitPool * inflation.rate;
      }
    }
    const poolAfterAccrual = openState.pools.baseBenefitPool + inflationAccrual;

    // 4. Payout: reimbursement (default) or cash indemnity. Single pool only.
    const isIndemnity = ltcPolicy.isIndemnityPolicyPayment === true;
    const proRatedCap = monthlyCap * (eligibleDays / DAYS_IN_MONTH);
    const eligibleCost = costMetrics.dailyCareCost * eligibleDays;

    let drawFromPool = 0;
    let indemnityPaid = 0;
    if (isIndemnity && poolAfterAccrual > 0 && eligibleDays > 0) {
      const indemnityAmount = Math.min(proRatedCap, poolAfterAccrual);
      drawFromPool = indemnityAmount;
      indemnityPaid = indemnityAmount;
    } else {
      const wouldPay = Math.min(eligibleCost, proRatedCap);
      drawFromPool = Math.min(wouldPay, poolAfterAccrual);
    }
    // Gross cash this row pays out. On indemnity this is the capped cash
    // regardless of cost; on reimbursement it is the reimbursed amount
    // (≤ eligible cost). The pool depletes by this gross cash; the surplus
    // model is a separate accounting reserve layered on top.
    const rowPayout = drawFromPool;

    // 4b. Indemnity-surplus accounting (per-row, unconditional). Cost-coverage
    //     is capped at eligible cost; cash above it banks as surplus and drains
    //     on later shortfall rows. On reimbursement-election policies the open
    //     surplus is always 0 and the reimbursed payout never exceeds eligible
    //     cost, so this is a numeric no-op (no accrual/drain).
    const surplusResult = applyIndemnitySurplus({
      openSurplus: openState.strategyState.indemnitySurplus,
      indemnityPayout: rowPayout,
      eligibleCost,
      fullCost: costMetrics.effectiveCareCost,
      // Single pool (legacy path): exhausted when the post-accrual balance is 0.
      policyExhausted: poolAfterAccrual <= 0,
    });

    // 5. Closing balances. The Contract Value floor is depleted by the LTC
    //    draw and clamped at 0 — the residual cannot go negative. Once
    //    cumulative draws exceed the floor it sits at 0 and the residual is 0.
    const closePool = poolAfterAccrual - drawFromPool;
    const closeDeathBenefit = Math.max(openState.pools.deathBenefit - drawFromPool, 0);
    const deathBenefitReduction = openState.pools.deathBenefit - closeDeathBenefit;
    // Floored on the row where the draw first pushed the residual to 0 and we
    // clamped it (the draw exceeded the remaining floor). Gated on a positive
    // opening floor so post-exhaustion rows (floor already 0) don't re-flag.
    const deathBenefitFloored =
      openState.pools.deathBenefit > 0 &&
      closeDeathBenefit === 0 &&
      drawFromPool > deathBenefitReduction;

    const facts: MonthlyCareFacts = {
      nominalCareCost: costMetrics.nominalCareCost,
      effectiveCareCost: costMetrics.effectiveCareCost,
      dailyCareCost: costMetrics.dailyCareCost,
      careCostInflationYears: costMetrics.careCostInflationYears,
      projectedCareYear: costMetrics.projectedCareYear,
      non2AdlDays,
      coverageEligibleDays: eligibleDays,
      waitingPeriodDaysConsumedHome,
      waitingPeriodDaysConsumedFacility,
      appliedMonthlyBenefitCap: monthlyCap,
      appliedCapPoolSource: 'base',
    };

    const payout: PayoutSummary = {
      ltcCoveragePaid: surplusResult.ltcCoveragePaid,
      ltcPayoutToCost: surplusResult.ltcPayoutToCost,
      ineligibleCost: costMetrics.effectiveCareCost - surplusResult.ltcPayoutToCost,
      indemnityPayout: indemnityPaid,
    };

    const deltas: MonthlyDeltas<ForecareDeltas> = {
      payout,
      base: { depletion: drawFromPool, inflationAccrual },
      eob: { depletion: 0, inflationAccrual: 0 },
      deathBenefit: { reduction: deathBenefitReduction, flooredAtMinimum: deathBenefitFloored },
      strategyDeltas: {
        ...emptyForecareDeltas(),
        indemnitySurplusAccrued: surplusResult.surplusAccrued,
        indemnitySurplusApplied: surplusResult.surplusApplied,
      },
    };

    const closeState: MonthlyState<ForecareState> = {
      pools: {
        baseBenefitPool: closePool,
        extensionOfBenefitsPool: 0,
        deathBenefit: closeDeathBenefit,
      },
      waitingPeriod: { homeDays: closeHomeWp, facilityDays: closeFacilityWp },
      strategyState: { indemnitySurplus: surplusResult.closeSurplus },
    };

    const status: MonthlyStatus = {
      ltcPoolExhausted: closePool <= 0,
      clientPoolExhausted: closePool <= 0,
      epSatisfied: closeRelevantWp === 0,
    };

    const minGuaranteed = ltcPolicy.policyMinimumGuaranteedBenefitAmount ?? 0;
    const milestones = deriveStandardMilestones<ForecareState, ForecareDeltas>({
      context,
      facts,
      openState,
      closeState,
      deltas,
      priorMonth,
      retroactiveLumpPaid: 0,
      minimumGuaranteedDeathBenefit: minGuaranteed,
      clientHasStartedLtc: clientAsClient.intakeSurvey?.clientHasStartedLtc ?? false,
      hasInflationProtection: inflation.rate > 0,
    });

    const ineligibilityInput = {
      careEnvironment: phaseCarePlan.appliedCareEnvironment,
      facts,
      openState,
      closeState,
      payout,
    };
    const ineligibilityReason = deriveIneligibilityReason<ForecareState>(ineligibilityInput);
    const ineligibleDayBreakdown = deriveIneligibleDayBreakdown<ForecareState>(ineligibilityInput);

    return {
      context,
      facts,
      openState,
      state: closeState,
      deltas,
      events: { status, milestones, ineligibilityReason, ineligibleDayBreakdown },
    };
  },

  // The residual account value left after LTC draws — the non-LTC payout.
  finalizeDeathBenefit({ closingState }: FinalizeDeathBenefitInput<ForecareState>): number {
    return closingState.pools.deathBenefit;
  },
};
